import axios, { AxiosInstance } from 'axios';
import { config } from '@/config';
import { PaymentMethod } from '@stripe/stripe-js';

export class ApiClient {
  private jwt!: string;
  private authenticatedRequest!: AxiosInstance;

  constructor(jwt?: string) {
    if (jwt) {
      this.jwt = jwt;
      this.buildAuthenticatedRequest();
    }
  }

  private buildAuthenticatedRequest() {
    this.authenticatedRequest = axios.create({
      baseURL: `${config.apiBaseURL}`,
      headers: {
        Authorization: `Bearer ${this.jwt}`,
      },
    });
  }

  public setJwt(jwt: string) {
    this.jwt = jwt;
    this.buildAuthenticatedRequest();
  }

  public async getStripePlans() {
    const { data } = await this.authenticatedRequest({
      method: 'GET',
      url: '/stripe/plans',
    });

    return data;
  }

  public async getCards(): Promise<Array<PaymentMethod>> {
    const { data } = await this.authenticatedRequest({
      method: 'GET',
      url: '/stripe/payment-methods/cards',
    });

    return data;
  }

  public async getSepaDirectDebits(): Promise<Array<PaymentMethod>> {
    const { data } = await this.authenticatedRequest({
      method: 'GET',
      url: '/stripe/payment-methods/sepa-direct-debits',
    });

    return data;
  }

  public async createSetupIntent(): Promise<{ clientSecret: string }> {
    const { data } = await this.authenticatedRequest({
      method: 'POST',
      url: '/stripe/setup-intent',
    });

    return data;
  }

  public async createSubscriptionSchedule({
    priceId,
    paymentMethodId,
  }: {
    priceId: string;
    paymentMethodId: string;
  }): Promise<any> {
    const { data } = await this.authenticatedRequest({
      method: 'POST',
      url: '/stripe/subscription-schedule',
      data: {
        priceId,
        paymentMethodId,
      },
    });
  }

  public async getMe() {
    const { data } = await this.authenticatedRequest({
      method: 'GET',
      url: '/users/me',
    });

    return data;
  }

  public async updateOrganization({
    _id,
    organization,
  }: {
    _id: string;
    organization: {
      title: string;
      addressStreet: string;
      addressStreetNumber: string;
      addressZipCode: string;
      addressCity: string;
      addressCountry: string;
    };
  }) {
    const { data } = await this.authenticatedRequest({
      method: 'PUT',
      url: `/organizations/${_id}`,
      data: organization,
    });

    return data;
  }

  public async getLatestPaymentIntent() {
    const { data } = await this.authenticatedRequest({
      method: 'GET',
      url: '/stripe/latest-payment-intent',
    });

    return data;
  }
}
